/* eslint-disable complexity */
import { useEffect, useMemo, useState } from "react";

import { AGGCustomerStoriesData } from "src/clients/agg-client/agg-client-types";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import UFOSegment from "@atlaskit/react-ufo/segment";

import { CustomerStory } from "@/content/customerStories/customerStories.types";
import { TypographyStyles } from "@/libraries/wpl/foundations/src/typography-styles";
import { GlobalFooter } from "@/patterns/global-footer/src/global-footer";
import { GlobalNav as GlobalNavigation } from "@/patterns/global-nav/src/nav-global";
import { CarouselSection } from "@/templates/customers/customers-landing-page/sections/carousel-section";
import { CustomerStoriesSection } from "@/templates/customers/customers-landing-page/sections/customer-stories";
import { CustomerStoryFilters } from "@/templates/customers/customers-landing-page/sections/customer-story-filters";
import { FutureOfTeamwork } from "@/templates/customers/customers-landing-page/sections/future-of-teamwork";
import { GlobalStatsSection } from "@/templates/customers/customers-landing-page/sections/stats-section";
import {
    areFiltersAreActive,
    CustomerStoryFilterObject,
    defaultCustomerStoryFilterOptionsObject,
    filterCustomerStories,
    getFilterEventProps,
    getFilterOptions,
    handleFallbackCustomerStories,
    ParsedCustomerStories,
    SubFilterObject
} from "@/templates/customers/customers-landing-page/utils";
import { flattenTemplateImageAssets } from "@/utils/content-queries/get-content-data";

const STORIES_AT_A_TIME = 10;

const productOrder = [
    "Jira",
    "Jira Service Management",
    "Jira Align",
    "Jira Product Discovery",
    "Confluence",
    "Trello",
    "Bitbucket",
    "Atlassian Access",
    "Guard",
    "Statuspage",
    "Compass",
    "Bamboo"
];
const orgSizeOrder = [
    "Small/Medium", // Small/Medium
    "Enterprise", // Enterprise
    "Large Enterprise" // Large Enterprise
];

export const getDefaultFilterObject = (
    customers: ParsedCustomerStories,
    filterByEnterprise: boolean
): CustomerStoryFilterObject => {
    const industry: SubFilterObject = getFilterOptions(
        customers.filters?.industries
    );
    const region: SubFilterObject = getFilterOptions(
        customers.filters?.regions,
        "region"
    );
    const product: SubFilterObject = getFilterOptions(
        customers.filters?.products
    );
    const solution: SubFilterObject = getFilterOptions(
        customers.filters?.solutions
    );
    let teamSize: SubFilterObject = getFilterOptions(
        customers.filters?.teamSizes,
        "team size"
    );

    const defaulted = {
        ...defaultCustomerStoryFilterOptionsObject
    };

    if (Object.keys(industry).length) {
        defaulted.industry = industry;
    }
    if (Object.keys(region).length) {
        defaulted["more filters"] = {
            ...defaulted["more filters"],
            ...region
        };
    }

    const teamEnterpriseOverride: SubFilterObject = {
        Enterprise: {
            committedActive: true,
            stagedActive: true,
            grouping: "team size"
        }
    };

    if (filterByEnterprise) {
        teamSize = {
            ...teamSize,
            ...teamEnterpriseOverride
        };
    }

    if (Object.keys(teamSize).length) {
        defaulted["more filters"] = {
            ...defaulted["more filters"],
            ...teamSize
        };
    }
    if (Object.keys(solution).length) {
        defaulted.solution = solution;
    }
    if (Object.keys(product).length) {
        defaulted.product = product;
    }

    return defaulted;
};

export const parseCustomerStories = (data: AGGCustomerStoriesData) => {
    const filters = data?.filters?.edges.sort((a, b) =>
        a.node.context.name < b.node.context.name ? -1 : 1
    );

    const result = {
        // Ordering (sys_publishedAt_DESC) and filtering (publishedAt_exists: true)
        // are handled directly in the Contentful GraphQL query.
        customerStories: data?.customerStories?.edges.map((story) =>
            flattenTemplateImageAssets(story.node)
        ),
        // Get lists to populate filters, by category
        filters: {
            industries: filters
                ?.filter((filter) => filter?.node?.field === "industry")
                .map((filter) => filter?.node?.context),
            regions: filters
                ?.filter((filter) => filter?.node?.field === "region")
                .map((filter) => filter?.node?.context),
            solutions: filters
                ?.filter((filter) => filter?.node?.field === "solution")
                .map((filter) => filter?.node?.context),
            teamSizes: filters
                ?.filter((filter) => filter?.node?.field === "organizationSize")
                .map((filter) => filter?.node?.context)
                .sort((a, b) => {
                    const aIndex = orgSizeOrder.indexOf(a.name);
                    const bIndex = orgSizeOrder.indexOf(b.name);

                    if (aIndex < 0) {
                        return 1;
                    } else if (bIndex < 0) {
                        return -1;
                    }

                    return aIndex - bIndex;
                }),
            products: filters
                ?.filter((filter) => filter?.node?.field === "products")
                .map((filter) => filter?.node?.context)
                .sort((a, b) => {
                    const aIndex = productOrder.indexOf(a.name);
                    const bIndex = productOrder.indexOf(b.name);

                    if (aIndex < 0) {
                        return 1;
                    } else if (bIndex < 0) {
                        return -1;
                    }

                    return aIndex - bIndex;
                })
        }
    };

    return result;
};

export interface CustomerLandingPageProps {
    customerLanding: AGGCustomerStoriesData;
}

export const CustomersLandingPageRelayTemplate = ({
    customerLanding
}: CustomerLandingPageProps) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const filterByEnterpriseParam = getFilterByEnterpriseParam();
    const [filterByEnterprise, setFilterByEnterprise] = useState(
        filterByEnterpriseParam
    );

    const customers = parseCustomerStories(customerLanding);

    const [filterObjectValue, setFilterObjectValue] =
        useState<CustomerStoryFilterObject>(
            getDefaultFilterObject(customers, filterByEnterprise)
        );
    const [searchFilterString, setSearchString] = useState("");
    const [searchInputFieldFocused, setSearchInputFieldFocused] =
        useState(false);
    const [filterSearchhasRun, setFilterSearchHasRun] = useState(false);
    const [customerStoryList, updateCustomerStoryList] = useState<
        CustomerStory[]
    >(customers.customerStories);
    const [numberOfCustomerStories, setNumberOfCustomerStories] = useState(
        customerStoryList?.length
    );

    const [numberOfVisibleStories, setNumberOfVisibleStories] =
        useState(STORIES_AT_A_TIME);

    useEffect(() => {
        setNumberOfCustomerStories(customerStoryList?.length);
    }, [customerStoryList]);

    useEffect(() => {
        // if the URL parameter `filterByEnterprise` is true, and we have received customer stories
        // proceed to run a search with the enterprise filter
        if (filterByEnterprise && customers.customerStories.length > 0) {
            runSearchWithFilters(filterObjectValue, "");

            //set to false so that getDefaultFilterObject() ignores overriding the teamSize filter
            setFilterByEnterprise(false);
        }
        // Reason: Tech Debt - Disable needed to migrate from wac-ufo-segment to ufo-segment
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customers]);

    const clearAllFilters = () => {
        setFilterObjectValue(
            getDefaultFilterObject(customers, filterByEnterprise)
        );
        updateCustomerStoryList(customers.customerStories);
        setSearchString("");
        setSearchInputFieldFocused(false);
        setFilterSearchHasRun(false);
    };

    const clearSearchField = () => {
        setSearchString("");
        setSearchInputFieldFocused(false);
        runSearchWithFilters(filterObjectValue, "");
    };

    const setSearchStringWrapper = (searchString: string) => {
        setSearchString(searchString);
    };
    const loadMoreStories = () => {
        setNumberOfVisibleStories(numberOfVisibleStories + STORIES_AT_A_TIME);
    };

    const runSearchWithFilters = (
        filterObject: CustomerStoryFilterObject,
        searchString: string
    ) => {
        const normalizedSearchString = searchString.toLowerCase() ?? "";
        const filtersAreActive = areFiltersAreActive(filterObject);

        if (!filtersAreActive && normalizedSearchString.length === 0) {
            setFilterSearchHasRun(false);
            if (customers.customerStories.length !== numberOfCustomerStories) {
                updateCustomerStoryList(customers.customerStories);
            }
            return;
        }

        // Initial text based search

        const searchResults = customers.customerStories?.filter((story) => {
            if (searchFilterString?.length) {
                return (
                    story.title
                        ?.toLowerCase()
                        ?.includes(normalizedSearchString) ||
                    story.customerCompany?.name
                        ?.toLowerCase()
                        ?.includes(normalizedSearchString) ||
                    story.urlSlug
                        ?.toLowerCase()
                        ?.includes(normalizedSearchString) ||
                    story.customerCompany?.region?.name
                        ?.toLowerCase()
                        ?.includes(normalizedSearchString) ||
                    // @ts-expect-error -- industry/products types have optional fields not reflected in filter callback
                    story.customerCompany?.industry?.filter((industry) =>
                        industry.name
                            ?.toLowerCase()
                            ?.includes(normalizedSearchString)
                    )?.length ||
                    // @ts-expect-error -- industry/products types have optional fields not reflected in filter callback
                    story.products?.filter((product) =>
                        product.name
                            ?.toLowerCase()
                            ?.includes(normalizedSearchString)
                    )?.length ||
                    story.customerCompany?.organizationSize?.name
                        ?.toLowerCase()
                        ?.includes(normalizedSearchString)
                );
            }
            return true;
        });

        const filterResults = filterCustomerStories(
            searchResults,
            filterObject,
            "committedActive"
        );
        updateCustomerStoryList(filterResults);
        setFilterSearchHasRun(true);

        createAnalyticsEvent({
            eventComponent: "customerFilter",
            eventType: "ui",
            action: "clicked",
            actionSubject: "component",
            attributes: {
                filtersApplied: getFilterEventProps(filterObject),
                page_path: "/customers"
            }
        }).fire();
    };

    // In order to get the number of Customer Stories matching the stagedActive filter
    // we need to we'll call the filterCustomerStories function with the stagedActive flag
    const numberOfCustomerStoriesFilteredByStagedActive = useMemo(() => {
        return filterCustomerStories(
            customers.customerStories,
            filterObjectValue,
            "stagedActive"
        )?.length;
    }, [filterObjectValue, customers.customerStories]);

    let fallbackCustomerStoryList = useMemo(() => {
        return [] as CustomerStory[];
    }, []);

    fallbackCustomerStoryList = useMemo(() => {
        return handleFallbackCustomerStories(customers.customerStories);
    }, [customers.customerStories]);
    return (
        <UFOSegment name="customers-page">
            <GlobalNavigation isShowTryNowLink={true} productKey="customers" />
            <TypographyStyles>
                <CarouselSection />
                <CustomerStoryFilters
                    filterObject={filterObjectValue}
                    clearAllFilterButtonClickHandler={clearAllFilters}
                    setFilterObjectValue={setFilterObjectValue}
                    runSearchWithFilters={runSearchWithFilters}
                    numberOfCustomerStoriesByCommittedValues={
                        numberOfCustomerStories
                    }
                    numberOfCustomerStoriesByStagedValues={
                        numberOfCustomerStoriesFilteredByStagedActive
                    }
                    searchStringValue={searchFilterString}
                    searchInputFieldFocused={searchInputFieldFocused}
                    setSearchInputFieldFocused={setSearchInputFieldFocused}
                    clearSearchField={clearSearchField}
                    setSearchString={setSearchStringWrapper}
                    hasFilterSearchRun={filterSearchhasRun}
                />
                <CustomerStoriesSection
                    totalNumberOfCustomerStories={numberOfCustomerStories}
                    customerStoryList={customerStoryList?.slice(
                        0,
                        numberOfVisibleStories
                    )}
                    fallbackCustomerStoryList={fallbackCustomerStoryList}
                    filterSearchHasRun={filterSearchhasRun}
                    loadMoreStories={loadMoreStories}
                />
                <GlobalStatsSection />
                <FutureOfTeamwork />
            </TypographyStyles>
            <GlobalFooter />
        </UFOSegment>
    );
};
const getFilterByEnterpriseParam = () => {
    const { searchParams } = new URL(window.location.href);
    const enterpriseParam: string | null =
        searchParams.get("filterByEnterprise");

    return enterpriseParam === "true";
};
